import { useMemo, useState } from "react";
import Layout from "../../Shared/Layout";
import SelfRepairGuidesLayout from "./SelfRepairGuidesLayout";
import { Alert } from "@mui/material";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/captions.css";
import { Trans } from "../../utils/i18n";
import Markdown from "react-markdown";
import { selfRepairGuideConfig } from "../../config/selfRepairGuide";
import { FormState } from "../../FormComponents/FormState";
import FormPage from "../../Shared/FormPage";
import { SelfRepairFlowStep } from "./SelfRepairFlowStep";
import { SelfRepairFlowMetaData } from "./SelfRepairFlowMetaData";
import { SelfRepairTool } from "./SelfRepairTool";
import { useSyncOrderState } from "../../hooks/useSyncOrderState";

export default function SelfRepairFlowPage({
    categories,
    category: selectedCategory,
    selfrepairflow: selectedSelfRepairFlow,
    user,
    requesttypes,
    orderControllerConfig,
}) {
    const [slides, setSlides] = useState([]);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [index, setIndex] = useState(0);
    const formConfig = selfRepairGuideConfig;
    const defaultState = useMemo(() => {
        return {
            orderControllerConfig: orderControllerConfig
                ? {
                      enableStepper: !!formConfig.enableStepper,
                      ...orderControllerConfig,
                  }
                : { enableStepper: !!formConfig.enableStepper },
            selectedProducts: selectedSelfRepairFlow?.products,
        };
    }, []);
    const { defaultFormValues, defaultOrderEntryState, isMetadataLoading, hasOrderNumber } = useSyncOrderState({
        queryKey: ["selfrepair"]
    });

    const onCloseLightbox = () => {
        setIsLightboxOpen(false);
        setSlides([]);
        setIndex(0);
    };

    const openLightbox = ({ index, slides }) => {
        setSlides(slides);
        setIndex(index);
        setIsLightboxOpen(true);
    };

    // Since we want to initialize the form and the order state based on the metadata
    // of the order, we need to wait until the metadata is loaded.
    if (hasOrderNumber && isMetadataLoading) {
        return null;
    }

    return (
        <SelfRepairGuidesLayout
            showSearch={false}
            backgroundImageUrl={selectedSelfRepairFlow.image}
            title={selectedSelfRepairFlow.name}
            showBackButton
            backButtonUrl={route("selfrepair.guides.showCategory", {
                category: selectedCategory.slug,
            })}
        >
            <div className="space-y-12">
                <SelfRepairFlowMetaData
                    complexity={selectedSelfRepairFlow.complexity}
                    estimatedTime={selectedSelfRepairFlow.time}
                    targetUser={selectedSelfRepairFlow?.target_user}
                />

                <main>
                    <h1 className="text-4xl my-0">{selectedSelfRepairFlow?.name}</h1>

                    {selectedSelfRepairFlow?.description && (
                        <Markdown>
                            {selectedSelfRepairFlow?.description}
                        </Markdown>
                    )}

                    <div className="space-y-12">
                        {selectedSelfRepairFlow?.selfRepairTools?.length >
                            0 && (
                            <div>
                                <h2 className="text-2xl">
                                    <Trans i18nKey="selfRepair.guide.requiredTools.title" />
                                </h2>
                                <p>
                                    <Trans i18nKey="selfRepair.guide.requiredTools.description" />
                                </p>

                                <div className="flex flex-wrap gap-4">
                                    {selectedSelfRepairFlow?.selfRepairTools?.map(
                                        (tool) => (
                                            <SelfRepairTool
                                                image={tool.image}
                                                name={tool.name}
                                                key={tool.id}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                        <ol className="list-none p-0 ml-2 border-l border-gray-200">
                            {selectedSelfRepairFlow?.steps?.map(
                                (step, index) => (
                                    <li key={step.id}>
                                        <SelfRepairFlowStep
                                            id={step.id}
                                            name={step.name}
                                            description={step.description}
                                            images={step.images ?? []}
                                            substeps={step.children ?? []}
                                            onClickImage={({
                                                index,
                                                images,
                                                name,
                                                description,
                                            }) => {
                                                openLightbox({
                                                    index,
                                                    slides: images.map(
                                                        (image) => ({
                                                            src: image.path,
                                                            title: name,
                                                            description: (
                                                                <Markdown>
                                                                    {
                                                                        description
                                                                    }
                                                                </Markdown>
                                                            ),
                                                        })
                                                    ),
                                                });
                                            }}
                                        />
                                    </li>
                                )
                            )}
                        </ol>
                    </div>
                </main>

                {selectedSelfRepairFlow?.warnings && (
                    <Alert severity="warning">
                        <div className="font-semibold text-base">
                            <Trans i18nKey="selfRepair.guide.warnings" />
                        </div>

                        <Markdown>{selectedSelfRepairFlow?.warnings}</Markdown>
                    </Alert>
                )}

                <div>
                    <h2 className="text-2xl">
                        <Trans i18nKey="selfRepair.guide.buyRequiredParts" />
                    </h2>
                    <FormState defaultState={{
                        ...defaultState,
                        ...defaultOrderEntryState,
                    }}>
                        <FormPage
                            requesttypes={requesttypes}
                            orderEntryConfig={formConfig}
                            endpointUrl={route("order")}
                            SuccessPage={formConfig?.success}
                            defaultValues={{
                                ...formConfig.defaultValues,
                                ...defaultFormValues,
                            }}
                        />
                    </FormState>
                </div>
            </div>

            <Lightbox
                carousel={{
                    finite: true,
                }}
                captions={{
                    descriptionMaxLines: 5,
                    descriptionTextAlign: "center",
                    showToggle: true,
                }}
                controller={{
                    closeOnPullUp: true,
                    closeOnBackdropClick: true,
                    closeOnPullDown: true,
                }}
                plugins={[Zoom, Thumbnails, Fullscreen, Captions]}
                slides={slides}
                open={isLightboxOpen}
                close={onCloseLightbox}
                index={index}
                zoom={{
                    maxZoomPixelRatio: 5,
                    scrollToZoom: true,
                }}
            />
        </SelfRepairGuidesLayout>
    );
}

SelfRepairFlowPage.layout = (page) => (
    <Layout children={page} wrapperStyle={{ paddingTop: 0 }} />
);
